// ============================================================================
// view.feed — the poultry feed ledger, off App's boot entirely
// ============================================================================
// /broiler/feed rendered from SEVEN values that arrived through three separate
// App channels, which is more of the global boot than any other single surface
// consumed in one place:
//
//   ppp-v4                          BatchesContext.batches
//   ppp-feed-orders-v1              a BroilerEntry prop
//   ppp-poultry-feed-inventory-v1   a BroilerEntry prop
//   layer_batches                   LayerContext.layerBatches
//   layer_housings                  LayerContext.layerHousings
//   layer_dailys (full history)     LayerContext.allLayerDailys
//   poultry_dailys (full history)   DailysRecentContext.broilerDailys
//
// `broiler.feed.core` owns all seven, which is what lets `feed` retire from
// FOUR of the five frozen consumer lists in
// tests/fixtures/former-eager-dependencies.json — app_store, layer_batches,
// layer_housings and poultry_dailys. It was in every one of them it could be
// in; pig_dailys is the only former-eager read this page never touched.
//
// WHY THIS IS PERMANENT AND BOOT-RELEASED.
//
// BroilerEntry passes no App props to this route. `broiler.feed.core` owns all
// seven render reads; the collapse set is local UI state; and its two writes are
// direct, using the same Supabase client and save-status channel as App's former
// delegate. The release therefore has no hidden data or write dependency on the
// global loader.
//
// The shared ppp-feed-orders-v1 document was the historical blocker. A whole
// document write from either feed page could overwrite the other page's map.
// `saveFeedOrdersPatch` now merges only starter/grower/layerfeed onto a SAVE-TIME
// read, preserving the pig map and failing closed if that read is unavailable.
// The physical count goes through `saveAppStoreBlob`; neither operation needs
// an App callback or mirror to remain correct.
//
// `{view: 'feed', paths: ['/broiler/feed']}` is an exact, unsplit route row.
// Its release is fenced by raw-path equality in releasedSurfaceBoundary, has a
// reviewed ledger row and receipt, and mounts BroilerFeedView with zero props.
//
// ONE THING THIS SURFACE DOES NOT CLAIM.
//
//   IT DOES NOT RETIRE THE LayerContext MIRROR. `legacy.context.layer.batches`
//   / `.housings` / `.allDailys` are still published by App's boot for the
//   surfaces that still read them. This surface simply stopped being one of
//   them. THE READER LIST HERE WAS ALLOWED TO GO STALE and is deliberately not
//   restated: it named view.layersHome and view.animalHistory, and both have
//   since migrated to permanent with `residualLegacy: []` — layersHome reads
//   all five of its inputs through layer.home.core, and is boot-released by
//   the wave-two program-home slice. Re-derive the live readers from
//   RESIDUAL_LEGACY_IDS in the manifest rather than trusting a sentence.
//
// AND ONE THING THE CENSUS GOT WRONG. view.feed was described as one of three
// surfaces reading `legacy.context.layer.groups`. It never read it: BroilerFeedView
// destructured layerBatches, layerHousings and allLayerDailys from useLayer()
// and never `layerGroups`. The mirror's live readers are elsewhere.
// ============================================================================

import type {MigratedSurfaceDeclaration} from '../surfaceDataManifest';

const declaration: MigratedSurfaceDeclaration = Object.freeze({
  surface: 'view.feed',
  obligations: Object.freeze([Object.freeze({id: 'broiler.feed.core', owner: 'permanent'})]),
  residualLegacy: Object.freeze([] as const),
});

export default declaration;
